import { create } from 'zustand';
import type { IdMatchType } from '@/features/auth';
import { createJSONStorage, persist } from 'zustand/middleware';

/**
 * Client-side session state for the anonymous provider access model (Provider ID + CAPTCHA, no
 * registration and no password in v1).
 *
 * This store holds UI state only. It is NOT an authorization boundary: every protected read is
 * re-verified server-side by portal-backend against the policy record. Never gate data access on it.
 *
 * The state is persisted to sessionStorage so a page refresh does not throw the provider back to the
 * login screen (PP-003). sessionStorage, not localStorage: it dies with the tab, so a shared
 * workstation never resurrects a previous provider's session, and it is scoped per tab. The inactivity
 * timeout still applies — `startedAt` survives the refresh with the rest of the state.
 */
interface SessionState {
  readonly providerId: string | null;
  /**
   * Which identifier portal-backend matched, so the header can name it (PP-012). A label, not a
   * credential and not an authorization input — the backend re-verifies every protected read regardless.
   */
  readonly idMatchType: IdMatchType | null;
  readonly startedAt: number | null;
  signIn: (providerId: string, idMatchType?: IdMatchType | null) => void;
  signOut: () => void;
}

export const useSessionStore = create<SessionState>()(
  persist(
    (set) => ({
      providerId: null,
      idMatchType: null,
      startedAt: null,
      signIn: (providerId, idMatchType = null): void => {
        set({ providerId, idMatchType, startedAt: Date.now() });
      },
      signOut: (): void => {
        set({ providerId: null, idMatchType: null, startedAt: null });
      },
    }),
    {
      name: 'portal-session',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);
